import { ResumeDto, UpdateResumeDto } from "@reactive-resume/dto";
import { defaultSections } from "@reactive-resume/schema";
import { useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";

import { toast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

const normalizeResumePayload = (data: UpdateResumeDto): UpdateResumeDto => {
  const resumeData = data.data;
  if (!resumeData?.sections) return data;

  return {
    ...data,
    data: {
      ...resumeData,
      sections: {
        ...defaultSections,
        ...resumeData.sections,
        custom: resumeData.sections.custom ?? defaultSections.custom,
      },
    },
  };
};

export const updateResume = async (data: UpdateResumeDto) => {
  const payload = normalizeResumePayload(data);

  const response = await axios.patch<ResumeDto, AxiosResponse<ResumeDto>, UpdateResumeDto>(
    `/resume/${payload.id}`,
    payload,
  );

  queryClient.setQueryData<ResumeDto>(["resume", { id: response.data.id }], response.data);

  queryClient.setQueryData<ResumeDto[]>(["resumes"], (cache) => {
    if (!cache) return [response.data];
    return cache.map((resume) => {
      if (resume.id === response.data.id) return response.data;
      return resume;
    });
  });

  return response.data;
};

/**
 * The autosave. Every mutator in `stores/resume.ts` calls this and nothing awaits it.
 *
 * ⚠️ THE TRY/CATCH IS THE POINT. `debounce` hands its callback's promise to nobody, so before this
 * wrapper existed a rejected PATCH was swallowed whole: no toast, no console line, no retry. The
 * store had already applied the edit optimistically, so the preview updated and — in the AI
 * optimization flow — the score donut moved, while the server kept the old document. "My accepted
 * suggestions didn't save" and "saving is broken" are the same bug seen from the outside, and
 * neither was diagnosable from the UI.
 *
 * Reported rather than retried: the two ways this actually fails are a locked résumé (the API
 * rejects the write outright — retrying cannot help) and being offline, where the next edit
 * re-sends the whole document anyway because the payload is the full résumé, not a diff.
 */
const autosaveResume = async (data: UpdateResumeDto) => {
  try {
    return await updateResume(data);
  } catch (error) {
    const message =
      (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
      (error as Error)?.message;

    toast({
      variant: "error",
      // eslint-disable-next-line lingui/no-unlocalized-strings
      title: "Couldn't save your changes",
      // eslint-disable-next-line lingui/no-unlocalized-strings
      description: message || "Your latest edits are not saved yet. Please try again.",
    });
  }
};

export const debouncedUpdateResume = debounce(autosaveResume, 500);

/**
 * Write a pending autosave out NOW.
 *
 * The debounce has a 500ms trailing window and no unmount behaviour of its own, so an edit made
 * inside that window is lost if the student leaves the builder — closing the tab, or navigating
 * back to the dashboard, both of which are exactly what someone does right after accepting the last
 * AI suggestion. `useResumeSaveFlush` calls this on `pagehide` and on unmount.
 */
export const flushResumeSave = () => {
  void debouncedUpdateResume.flush();
};

export const useUpdateResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: updateResumeFn,
  } = useMutation({
    mutationFn: updateResume,
  });

  return { updateResume: updateResumeFn, loading, error };
};
